import type { OHLCV } from "@luxalgo/vela";
import type { ExecutionCandle } from "../../types/trade";
import { executionCandleRowTime } from "../executionCandlesNormalize";

/** Parse candle time to epoch milliseconds (Vela canonical unit). */
export function executionCandleTimeToMillis(raw: string | number | null | undefined): number | null {
  if (raw == null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw > 1e12 ? Math.floor(raw) : Math.floor(raw * 1000);
  }
  const s = String(raw).trim();
  if (!s) return null;
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

/** Map Tessera execution candles → Vela offline OHLCV bars (epoch ms). */
export function executionCandlesToVelaBars(
  candles: readonly ExecutionCandle[] | null | undefined
): OHLCV[] {
  const list = candles ?? [];
  const out: OHLCV[] = [];
  for (const c of list) {
    const time = executionCandleTimeToMillis(executionCandleRowTime(c));
    if (time == null) continue;
    if (c.open == null || c.high == null || c.low == null || c.close == null) continue;
    const open = Number(c.open);
    const high = Number(c.high);
    const low = Number(c.low);
    const close = Number(c.close);
    if (![open, high, low, close].every(Number.isFinite)) continue;
    const vol = c.volume != null ? Number(c.volume) : undefined;
    const bar: OHLCV = { time, open, high, low, close };
    if (vol != null && Number.isFinite(vol)) bar.volume = vol;
    out.push(bar);
  }
  out.sort((a, b) => a.time - b.time);
  // Dedupe by time (keep last).
  const deduped: OHLCV[] = [];
  for (const bar of out) {
    const prev = deduped[deduped.length - 1];
    if (prev && prev.time === bar.time) {
      deduped[deduped.length - 1] = bar;
    } else {
      deduped.push(bar);
    }
  }
  return deduped;
}

/** Tessera candle_type UI values → Vela timeframe strings. */
export function tesseraCandleTypeToVelaTimeframe(candleType: string | null | undefined): string {
  switch (String(candleType ?? "").trim().toLowerCase()) {
    case "m":
    case "1m":
      return "1";
    case "5m":
      return "5";
    case "15m":
      return "15";
    case "30m":
      return "30";
    case "h":
    case "1h":
      return "60";
    case "4h":
      return "240";
    case "1d":
    case "d":
      return "1D";
    default:
      return "60";
  }
}

/** Vela timeframe string → Tessera candle_type UI value. */
export function velaTimeframeToTesseraCandleType(tf: string | null | undefined): string {
  switch (String(tf ?? "").trim()) {
    case "1":
      return "m";
    case "5":
      return "5m";
    case "15":
      return "15m";
    case "30":
      return "30m";
    case "60":
      return "h";
    case "240":
      return "4h";
    case "1D":
    case "D":
      return "1d";
    default:
      return "h";
  }
}

export const TESSERA_VELA_TIMEFRAMES = ["1", "5", "15", "30", "60", "240"] as const;
